import React, {useRef} from "react";
import {Row, Col, Container, Button, Carousel} from "react-bootstrap";
import Formsy from "formsy-react";
import {useAsyncSetState} from "use-async-setstate";
import {useQuery, useMutation} from "@apollo/client";
import {getOperationName} from "apollo-utilities";
import moment from "moment-mini";
import {catchErrorMessage} from "../utils/error-message";
import Modal from "../bootstrap/modal";
import {getProductsQuery, getProductsResult} from "../logic/products";
import {
  getCurrentUserQuery,
  changePlanMutation,
  changePlanResult,
} from "../logic/user";
import Loader from "../bootstrap/loader";
import Main from "../main";
import Layout from "../section/layout";
import {console} from "window-or-global";
import Addons from "../../controls/register/addons-display";
import Input from "../bootstrap/input";
import { NON_INTERNET_PRODUCT_TYPES_TO_SHOW } from "../../utils/constant";

const Index = (props) => {
  return (
    <Main>
      <Layout>
        <DataLayer {...props} />
      </Layout>
    </Main>
  );
};

const DataLayer = (props) => {
  const {onClose, user} = props;
  const buildingCode = user?.building?.code;
  const [voucherCode, setVoucherCode] = useAsyncSetState(null);
  const getProducts = useQuery(getProductsQuery, {
    variables: {
      buildingCode,
      showHidden: true,
      voucherCode,
    },
  });

  if (getProducts?.loading) {
    return <ModalLoader onClose={onClose} />;
  }

  const products = getProductsResult(getProducts);

  return (
    <ChangePlan
      buildingCode={buildingCode}
      products={products}
      voucherCode={voucherCode}
      setVoucherCode={(data) => setVoucherCode(data)}
      {...props}
    />
  );
};


const ChangePlan = (props) => {
  const {onClose, selectedOrder, products, voucherCode, setVoucherCode, buildingCode, user} = props;
  const [state, setState] = useAsyncSetState({
    success: false,
    confirm: false,
    processing: false,
    error: null,
    data: null,
  });

  const noAccount = (user?.accounts?.edges || []).length === 0;
  const [voucherInput, setVoucherInput] = useAsyncSetState(voucherCode || "");
  const [successMessage, setSuccessMessage] = useAsyncSetState("");
  const [selectedProduct, setProduct] = useAsyncSetState(null);
  const [changePlan] = useMutation(changePlanMutation);
  const plans = products.filter((value) => value.type === "internet" && !value.hidden);
  const orderProducts = (selectedOrder?.orderItems?.edges || []).map(
    (orderItem) => orderItem?.node?.product
  );

  const internetPlan = (orderProducts).find((oi) => oi?.type === "internet");

  const genericAddons = orderProducts.filter(
    (p) => {
      return NON_INTERNET_PRODUCT_TYPES_TO_SHOW.indexOf(p.type) > -1 && products.filter(a=>a.id === p.id).length > 0;
    }
  );
  const hardwareAddons = orderProducts.filter((p) => p.type === "hardware");
  const activeProduct = orderProducts.find(
    (value) => value.type === "internet"
  );
  const defaultProductIndex = plans.findIndex(
    (p) => p.id === activeProduct?.id
  );
  const [index, setIndex] = useAsyncSetState(defaultProductIndex > -1 ? defaultProductIndex : 0);
  const [addons, setAddons] = useAsyncSetState(genericAddons);
  const [selectedProductIds, setSelectedProductIds] = useAsyncSetState(internetPlan?.id ? [internetPlan?.id] : []);
  const formRef = useRef(null);

  const handleOnAddProduct = (product) => {
    let newAddons = [...addons, product];
    newAddons = newAddons
      .filter((value, index) => newAddons.findIndex((na) => na.id === value.id) === index);
    // .filter((value) => value.type !== "hardware");

    return setAddons(newAddons);
  };

  const handleOnRemoveProduct = (product) => {
    return setAddons(addons.filter((ao) => ao.id !== product.id));
  }

  const handleSubmit = () => {
    return setState((prevState) => ({
      ...prevState,
      confirm: true,
      error: null,
    }));
  };

  const handlePurchase = async ({schedule = false}) => {
    try {
      await setState((prevState) => ({
        ...prevState,
        confirm: false,
        processing: true,
      }));

      const genericAddonProducts = (addons || []).map((ao) => ao.id);
      //TODO - Detect if no changes and skip updateOrder

      const changePlanResponse = await changePlan({
        variables: {
          orderId: selectedOrder?.id,
          productIds: [selectedProduct.id].concat(genericAddonProducts || []),
          voucherCode,
          schedule,
        },
        awaitRefetchQueries: true,
        refetchQueries: [getOperationName(getCurrentUserQuery)],
      });
      let order = changePlanResult(changePlanResponse);
      if (!order?.id || changePlanResponse.error) {
        console.log("err update failed", order, changePlanResponse);
        throw "Error - unable to continue";
      }
      if (order?.id) {
        const total = [selectedProduct]
          .concat(addons.filter(ao => !ao.once || (ao.once && !orderProducts.find(op => op.id === ao.id))) || [])
          .reduce((prev, value) => prev + Number(value?.value || 0), 0);

        if (schedule) {
          await setSuccessMessage(`Your order has been updated and you will be charged '$${total}' on the '${moment(order?.renewsAt).format("DD MMM YYYY")}'`);
        } else {
          await setSuccessMessage(`Your account has been successfully charged '$${total}' and your account will now renew on '${moment(order?.renewsAt).format("DD MMM YYYY")}'`);
        }
        return setState((prevState) => ({
          ...prevState,
          success: true,
          processing: false,
          confirm: false,
        }));
      }

      return setState((prevState) => ({
        ...prevState,
        processing: false,
        confirm: false,
        success: false,
        error: catchErrorMessage(changePlanResponse?.errors || "An error has occurred."),
      }));
    } catch (err) {
      console.error(err);
      return setState((prevState) => ({
        ...prevState,
        processing: false,
        error:
          err.message.replace(/(GraphQL error:)/gi, "") ||
          "An error has occurred.",
      }));
    }
  };

  return (
    <Modal
      title={"Change Plan"}
      show
      onClose={onClose}
    >
      <Container>
        {noAccount && (
          <Row>
            <Col>
              <div className="alert change-error-notification">
                {"You need to update your credit card information before you can purchase product"}
              </div>
            </Col>
          </Row>
        )}
        {state.error && (
          <Row>
            <Col>
              <div className="alert change-error-notification">
                {(state.error === "EPURCHASEFAILED") ? "Purchase Failed" : "Something Went Wrong"}
              </div>
            </Col>
          </Row>
        )}
        {(() => {
          if (state.processing) {
            return (
              <div className="processing-purchase-loading">
                <Loader />
                <p className="processing-label">{"Processing"}</p>
              </div>
            );
          }

          if (state.success && successMessage) {
            return (
              <div>
                <span>
                  {successMessage}
                </span>
                <Row className="vw-portal-button-popup-row justify-content-end">
                  <Col xs="auto">
                    <Button variant="darkblue" onClick={async() => {
                      await setSuccessMessage("");
                      return onClose();
                    }}>
                      {"Close"}
                    </Button>
                  </Col>
                </Row>
              </div>
            );
          }

          if (state.confirm) {
            const addOnsTotal = (addons.filter(ao => !ao.once || (ao.once && !orderProducts.find(op => op.id === ao.id))) || [])
              .reduce(
                (prev, value) => Number(value.value) + Number(prev),
                0
              );
            const monthly = (selectedProduct.value || 0) + (addOnsTotal || 0);
            return (
              <div>
                <div className="change-plan-purchase-notice mb-2">
                  {"If you purchase now you will be restarting your monthly period and you will be charged a total of "}
                  <span className="bold">{`$${monthly} `}</span> {/*It will include newly added hardware (if any) in the first month*/}
                  {"to continue."}
                </div>
                <div className="change-plan-purchase-notice">
                  {"Alternatively you can schedule your change and will be charged "}
                  <span className="bold">{`$${monthly} `}</span>
                  {" at the end of the month."}
                </div>
                <div className="change-plan-purchase-actions">
                    {selectedOrder?.status !== 2 && (
                      <Button
                        type="button"
                        className="btn-schedule-purchase"
                        disabled={state.processing}
                        onClick={async () => handlePurchase({schedule: true})}>
                        <i className="far fa-calendar mr-3" />
                        {"Scheduled Purchase"}
                      </Button>
                    )}
                    <Button
                      type="button"
                      className="btn-schedule-purchase"
                      disabled={state.processing}
                      onClick={async () => handlePurchase({schedule: false})}>
                      <i className="far fa-check mr-3" />
                      {"Purchase Now"}
                    </Button>
                </div>
                <div className="change-plan-purchase-cancel">
                    <Button
                      type="button"
                      className="btn-back-purchase"
                      disabled={state.processing}
                      onClick={async () => setState({...state, confirm: false})}>
                      <i className="far fa-arrow-left mr-3" />
                      {"Go Back"}
                    </Button>
                </div>
              </div>
            );
          }

          return (
            <Formsy ref={formRef} onValidSubmit={handleSubmit}>
              <div className="change-plan-voucher-section">
                <div className="change-plan-voucher-input">
                  <Input name="voucherCode" placeholder="Voucher Code" value={voucherInput} onChange={(e) => setVoucherInput(e?.target?.value || "")} />
                </div>
                <div className="change-plan-actions">
                  <Button
                    className="apply-voucher-btn" 
                    type="button" disabled={!voucherInput} onClick={(e) => {
                    e.preventDefault();
                    return setVoucherCode(voucherInput);
                  }}>
                    {"Apply"}
                  </Button>
                  <Button 
                    className="change-plan-clear-voucher" 
                    type="button" 
                    onClick={async (e) => {
                    e.preventDefault();
                    await setVoucherInput("");
                    return setVoucherCode("");
                  }}>
                    {"Clear"}
                  </Button>
                </div>  
              </div>

              <div className="change-plan-plans">
                <div className="plan-content-inner">
                  <div
                    className="change-plans-group">
                    <Carousel
                      activeIndex={index}
                      className="product-carousel"
                      interval={null}
                      nextIcon={
                        <i
                          className="fas fa-angle-right plan-change-carousel-control"
                          onClick={async () => {
                            const newIndex = index < plans.length - 1 ? index + 1 : 0;
                            await setSelectedProductIds(plans[newIndex]?.id ? [plans[newIndex]?.id] : []);

                            return setIndex(newIndex);
                          }}
                        />
                      }
                      prevIcon={(
                        <i
                          className="fas fa-angle-left plan-change-carousel-control"
                          onClick={async () => {
                            const newIndex = index > 0 ? index - 1 : (plans || []).length - 1;
                            await setSelectedProductIds(plans[newIndex]?.id ? [plans[newIndex]?.id] : []);
                            return setIndex(newIndex);
                          }}
                        />
                      )}>
                      {plans.map((product) => {
                        return (
                          <Carousel.Item key={product?.id}>
                            <div className="plan-card-product">
                              <div className="plan-card-container">
                                <div className="details">
                                  <div>{"Unlimited Data"}</div>
                                </div>
                                <div className="product-title">
                                  {product.name}
                                </div>
                                <div className="product-image">
                                  {getPlanImage(product.name)}
                                </div>
                                <div className="price-speed">
                                  <div
                                    className="price"
                                    style={{
                                      textDecoration: product.originalValue
                                        ? "line-through"
                                        : undefined,
                                    }}>
                                    {`$${
                                      product.originalValue
                                        ? product.originalValue
                                        : product.value
                                    }`}
                                  </div>
                                  <div className="per-month">
                                    {"Per Month"}
                                  </div>
                                  {(product || {}).voucherName && (
                                    <div className="product-voucher">
                                      {`$${product.value} ${product.voucherName}`}
                                    </div>
                                  )}
                                  <div className="speed">
                                    {product.description}
                                  </div>
                                </div>
                                <div className="sub-details">
                                  <div>{"Typical Evening Speed"}</div>
                                  <div>
                                    {`${getPlanSpeedLabel(product.name)} Mbps`}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Carousel.Item>
                        );
                      })}
                    </Carousel>
                  </div>
                </div>
              </div>

              <Row>
                <Col>
                  <Addons
                    buildingCode={buildingCode}
                    disableHardWare
                    onAddProduct={handleOnAddProduct}
                    onRemoveProduct={handleOnRemoveProduct}
                    addons={addons}
                    selectedProductIds={selectedProductIds}
                    voucherCode={voucherCode}
                  />
                </Col>
              </Row>

           
              {(!state.processing && !state.success && !state.confirm) && 
              <div className="change-plan-footer-actions">
                <div className="cancel-change-action">
                  <Button
                    className="cancel-change-plan"
                    onClick={async () => {
                      return onClose && onClose();
                    }}>
                    {"Cancel"}
                  </Button>
                </div>
                <div className="confirm-change-action">
                    <Button
                      className="apply-voucher-btn"
                      type="submit"
                      disabled={noAccount}
                      onClick={async() => {
                        await setProduct(plans[index]);
                        return handleSubmit();
                      }}>
                      {"Confirm"}
                    </Button>
                  </div>
              </div>}
         
            </Formsy>
          );
        })()}
      </Container>
    </Modal>
  );
};

const ModalLoader = ({onClose}) => {
  return (
    <Modal
      title={"Change Plan"}
      show
      onClose={onClose}>
      <div className="loading-products-loading">
        <div className="rotating-loader" />
        <p className="loading-text">{"Loading products..."}</p>
      </div>
    </Modal>
  );
};

const getPlanImage = (product) => {
  switch (product) {
    case "50/20 Mbps":
      return (
        <svg id="Object" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50">
        <defs>
        </defs>
        <path class="cls-1" d="M25.03,17.77c-1.87,0-3.68,0-5.48,0-.22,0-.59,0-.66-.01-.36-.11-.8-.28-.95-.56-.14-.25-.06-.77.12-1.04.93-1.41,1.92-2.78,2.89-4.16,2.32-3.31,4.63-6.62,6.95-9.93.16-.22.35-.46.58-.59.67-.38,1.38.13,1.39,1,.01,1.89,0,3.78,0,5.67,0,1.25,0,2.49,0,3.83.23,0,.45,0,.66,0,1.79,0,3.58,0,5.37,0,.47,0,.88.09,1.11.55.22.44.05.81-.21,1.17-3.29,4.69-6.57,9.38-9.85,14.08-.3.43-.66.73-1.21.56-.57-.18-.7-.64-.7-1.19.01-3.1,0-6.19,0-9.38Z"/>
        <path class="cls-1" d="M18.43,41.33c1.17-.26,2-.57,2.84-.59,2.72-.07,5.45-.02,8.17-.03.43,0,.91.1,1.29-.05,4.26-1.69,8.3-3.67,10.21-8.26.12-.29.06-.93-.11-1.02-.37-.19-.91-.24-1.31-.12-.31.1-.6.51-.73.85-.58,1.59-1.51,2.71-3.35,2.61-.24.75-.35,1.44-.66,2.03-.56,1.08-1.51,1.66-2.74,1.66-2.85.02-5.71,0-8.56.01-.91,0-1.58-.37-1.56-1.33.02-.94.68-1.32,1.59-1.32,2.59,0,5.19.03,7.78-.03.02,0,.04,0,.06,0,.74-.08.8-1.15.07-1.34-.01,0-.02,0-.04,0-1.01-.22-2.05-.32-3.08-.42-1.34-.13-2.61-.27-3.67-1.33-.46-.46-1.31-.66-2.01-.72-1.33-.11-2.71-.21-4.01.03-.98.18-2.01.73-2.76,1.41-.92.82-1.57,1.94-2.34,2.93-1.06,1.36-1.68,1.39-2.9.12-.12-.12-.24-.23-.47-.44-.51.62-1,1.21-1.48,1.8-.58.7-1.29.93-2.02.3-.68-.58-.57-1.3-.05-1.96.53-.68,1.09-1.34,1.63-2.02,1.66-2.07,1.84-2.09,3.81-.29.53-.67,1.06-1.33,1.58-2,1.3-1.68,2.99-2.58,5.13-2.56,1.34,0,2.69-.08,4.02.04.92.08,2.03.2,2.69.74,1.57,1.29,3.41,1.3,5.22,1.59.97.15,1.94.38,2.89.64,1.02.28,2.26-.25,2.78-1.25,1.08-2.09,2.61-2.93,4.58-2.53,2.19.45,3.39,2.56,2.6,4.65-.96,2.51-2.41,4.58-4.74,6.13-6.87,4.56-5.42,4.05-13.43,4.08-1.25,0-2.51-.01-3.76,0-.32,0-.64.1-1.24.21,1.43,1.35.63,2.31-.16,3.29-.52.64-.98,1.32-1.5,1.95-.55.67-1.25.8-1.94.25-.64-.52-.6-1.18-.17-1.85.48-.74,1.34-1.54,1.27-2.24-.08-.75-1.05-1.4-1.64-2.09-.56-.66-1.13-1.31-1.7-1.96-.62-.71-.84-1.48-.04-2.16.75-.64,1.44-.3,2.02.37.71.81,1.42,1.62,1.92,2.2Z"/>
      </svg>);
    case "100/20 Mbps":
    case "100/40 Mbps":
      return (
        <svg id="Object" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50">
          <defs>
          </defs>
          <path class="cls-2" d="M22.56,33.9v-.96c0-3.93-.02-7.87,0-11.8.02-2.2,1.84-3.6,3.84-3,1.23.37,2.05,1.42,2.08,2.78.04,1.57,0,3.15.02,4.72,0,1.55.46,2.2,1.89,2.73,1.44.54,2.89,1.07,4.33,1.62,2.23.85,3.17,2.68,2.57,5-.6,2.31-1.21,4.62-1.84,6.92-.54,1.97-1.93,3.06-3.97,3.08-2.45.03-4.9.02-7.35,0-1.69,0-2.95-.75-3.73-2.27-1.68-3.25-3.35-6.5-5.07-9.73-.36-.68-.25-1.08.36-1.51,1.82-1.28,4.15-.93,5.47.87.39.53.74,1.08,1.11,1.62.1-.02.2-.04.3-.07Z"/>
          <path class="cls-1" d="M25.79,3.6c11.42.17,19.14,11.62,14.93,22.18-.1.25-.19.5-.32.73-.45.82-1.33,1.13-2.13.75-.75-.35-1.05-1.15-.8-2.03.37-1.31.87-2.61,1.05-3.95.92-6.88-4.14-13.44-11-14.34-7.24-.96-13.83,4.03-14.74,11.11-.3,2.35.01,4.59.88,6.77.47,1.19.22,2.09-.69,2.47-.95.4-1.79-.07-2.31-1.3C6.2,15.42,14.29,3.44,25.79,3.6Z"/>
          <path class="cls-1" d="M25.76,9.89c6.46.05,11.14,6.14,9.53,12.4-.31,1.22-1.05,1.77-2.05,1.53-.97-.24-1.35-1.07-1.06-2.32.66-2.89-.41-5.68-2.79-7.28-2.25-1.51-5.32-1.49-7.55.05-2.33,1.61-3.37,4.37-2.71,7.22.29,1.25-.09,2.09-1.05,2.32-1.02.25-1.75-.32-2.07-1.62-1.57-6.3,3.23-12.36,9.75-12.3Z"/>
        </svg>);
    case "250/25 Mbps":
    case "250/100 Mbps":
      return (
        <svg id="Object" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50">
          <defs>
          </defs>
          <path class="cls-1" d="M42.92,22.33c-.55-4.73-3.17-9.13-7.18-12.07-3.12-2.29-6.83-3.5-10.53-3.5-1.06,0-2.11.1-3.15.3-9.76,1.89-15.91,10.44-14.61,20.33,1.1,8.44,8.85,15.24,17.32,15.24.06,0,.13,0,.19,0,.92-.01,1.5-.48,1.56-1.25.07-.97-.85-1.33-1.41-1.46-.5-.11-1.42-.4-1.99-1.03-1.74-1.93-2.24-4.36-2.72-6.72l-.12-.6h1.96c.99,0,1.93.01,2.88,0,.83-.02,1.38-.52,1.41-1.28.03-.75-.56-1.34-1.4-1.4-.34-.02-.68-.02-1.02-.02h-.39s-3.84,0-3.84,0v-7.14h10.72v1.26c-.01.53-.02,1.05,0,1.56.04.94.59,1.56,1.37,1.5.79-.03,1.3-.65,1.32-1.59,0-.58,0-1.17,0-1.76v-1.24s6.67-1.19,6.67-1.19l.19,1.77c.1.9.19,1.79.28,2.68.08.78.6,1.31,1.31,1.32.36,0,.69-.12.95-.37.25-.25.4-.58.4-.94.01-.86-.08-1.75-.16-2.42ZM11.95,17.18c1.54-2.66,3.62-4.65,6.38-6.08l.99-.52-2.09,8.15-5.64-.94.36-.62ZM10.03,26.05c.02-1.28.07-2.56.12-3.89l.08-1.9,6.79,1.2.07.43v6.81l-1.94-.44c-1.44-.33-2.86-.65-4.27-1.01-.46-.12-.85-.76-.84-1.19ZM18.35,38.33c-3.1-1.58-5.39-3.92-6.99-7.16l-.43-.86,6.41,1,2,7.54-1-.51ZM20.1,18.95l.1-.58c.43-2.5.91-5.34,2.71-7.63.65-.82,1.42-1.26,2.24-1.26h0c.84,0,1.64.45,2.32,1.31,1.38,1.73,1.9,3.75,2.4,6.03.07.32.12.64.17,1l.18,1.14h-10.12ZM33.12,18.76l-2.09-8.15.99.5c2.68,1.36,4.77,3.34,6.39,6.05l.38.63-5.66.97Z"/>
          <path class="cls-1" d="M38.84,34.53c1.03,0,2.11,0,3.19,0,1.37.01,2.18.74,2.15,1.9-.02,1.11-.8,1.77-2.15,1.79-.83.01-1.66.04-2.49,0-.65-.03-.93.16-.89.85.05.92.04,1.85,0,2.78-.04,1.16-.79,1.91-1.85,1.89-1.05-.01-1.8-.77-1.83-1.93-.03-1.15,0-2.3,0-3.59-1.14,0-2.22,0-3.29,0-1.39-.01-2.22-.7-2.22-1.83,0-1.11.87-1.85,2.21-1.86,1.05,0,2.1,0,3.3,0,0-1.22-.03-2.39,0-3.56.05-1.54,1.47-2.41,2.74-1.72.69.38.94,1,.95,1.76,0,1.09.02,2.17.03,3.26,0,.06.06.11.13.26Z"/>
        </svg>
      );
    case "1000/50 Mbps":
    case "1000/400 Mbps":
      return (
        <svg id="Object" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50">
          <defs>
          </defs>
          <path class="cls-1" d="M44.23,32.12c-1.49-2.34-3.41-4.26-5.78-5.73-.35-.22-.71-.38-.74-.93-.26-4.89-1.21-9.63-3.19-14.12-1.43-3.26-3.36-6.16-6.19-8.41-.61-.48-1.11-.53-1.74-.03-1.69,1.32-3.11,2.89-4.28,4.68-3.43,5.25-4.83,11.15-5.37,17.3-.06.7-.28,1.08-.87,1.44-3.98,2.49-6.69,5.96-7.97,10.51-.77,2.72-.74,5.49-.48,8.3,1.94-4.19,4.6-7.56,9.31-8.9.02.35.04.58.05.81.02.24.04.49.07.77,6.83-3.9,13.57-3.58,20.35.08.06-.57.1-1.01.16-1.53,4.69,1.42,7.31,4.84,9.25,9.05.13-1.37.17-2.71.14-4.04-.06-3.33-.94-6.42-2.74-9.23ZM34.46,31.09c-.02.4-.08.8-.14,1.3-4.68-2.12-9.36-2.25-14.09-.13-.05-.41-.11-.77-.12-1.13-.16-4.02-.03-8.01.85-11.95.72-3.23,1.84-6.31,3.84-9,.63-.84,1.4-1.58,2.1-2.37.36-.41.66-.38,1.06,0,1.88,1.74,3.2,3.85,4.18,6.18,1.23,2.91,1.88,5.96,2.19,9.1.27,2.67.24,5.33.13,8Z"/>
          <path class="cls-1" d="M27.05,39.86c2.38.11,4.47.88,6.19,2.57.53.52.57,1.07.13,1.52-.44.44-.98.41-1.52-.1-2.79-2.62-6.81-2.64-9.66-.05-.54.49-1.1.51-1.52.06-.42-.46-.37-1,.17-1.51,1.74-1.66,3.84-2.41,6.21-2.48Z"/>
          <path class="cls-1" d="M27.65,43.02c.96.02,2.3.56,3.41,1.65.48.47.51,1.01.12,1.44-.38.42-1.01.41-1.5-.01-1.68-1.48-3.64-1.49-5.34-.03-.49.42-1.12.42-1.5,0-.39-.44-.34-.96.13-1.44,1.03-1.04,2.51-1.61,4.67-1.6Z"/>
          <g>
            <path class="cls-1" d="M35.67,40.39c-1.61-1.63-3.54-2.74-5.78-3.21-.94-.2-1.86-.31-2.76-.32v1.99c2.55,0,5.09.96,7.03,2.84.5.48.91.58,1.35.31.61-.39.69-1.06.16-1.6Z"/>
            <path class="cls-1" d="M18.55,40.29c-.54.53-.47,1.21.14,1.6.43.28.85.19,1.35-.29,1.96-1.86,4.52-2.79,7.08-2.76v-1.99c-.88,0-1.8.09-2.75.28-2.24.45-4.19,1.53-5.82,3.14Z"/>
          </g>
          <g>
            <path class="cls-1" d="M23.01,21.67l3.18.02v1s-2.21-.01-2.21-.01l-.06.89c.1-.02.21-.04.31-.06.1-.02.23-.02.37-.02.26,0,.49.04.71.1.22.07.41.16.57.3.16.13.29.31.38.52.09.21.14.46.14.76,0,.28-.05.52-.15.74-.1.22-.23.41-.4.56-.17.15-.38.27-.62.35-.24.08-.51.12-.81.12-.42,0-.78-.07-1.09-.21-.31-.14-.59-.33-.84-.57l.69-.81c.2.18.39.32.59.42.2.1.41.15.64.15.27,0,.48-.06.63-.18s.23-.3.24-.54-.08-.4-.24-.53-.39-.19-.67-.19c-.16,0-.32.02-.46.05-.15.04-.28.08-.4.13l-.65-.44.16-2.54Z"/>
            <path class="cls-1" d="M31.17,26.73c-.37.16-.79.23-1.26.23-.4,0-.77-.07-1.1-.21-.33-.13-.62-.32-.85-.56-.24-.24-.42-.52-.55-.84-.13-.33-.19-.68-.19-1.06h0c0-.38.07-.73.21-1.05.14-.32.33-.6.57-.84.24-.24.53-.43.86-.57.33-.14.69-.21,1.09-.2.23,0,.44.02.63.05.19.03.36.08.53.14.16.06.31.13.46.22.14.09.28.19.41.3l-.72.86c-.1-.08-.2-.16-.29-.22-.1-.06-.2-.12-.31-.16-.11-.04-.22-.08-.34-.11-.12-.03-.26-.04-.41-.04-.21,0-.4.04-.58.13-.18.09-.34.2-.47.34-.14.14-.24.31-.32.51-.08.19-.12.4-.12.62h0c0,.25.04.47.11.67.07.2.18.37.32.52.14.15.3.26.49.35.19.08.4.12.63.13.42,0,.78-.1,1.07-.31v-.74s-1.14,0-1.14,0v-.98s2.25.01,2.25.01v2.24c-.28.22-.6.42-.97.57Z"/>
          </g>
        </svg>
      );
  }

  return (
    <svg id="Object" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50">
      <defs>
      </defs>
      <path class="cls-2" d="M22.56,33.9v-.96c0-3.93-.02-7.87,0-11.8.02-2.2,1.84-3.6,3.84-3,1.23.37,2.05,1.42,2.08,2.78.04,1.57,0,3.15.02,4.72,0,1.55.46,2.2,1.89,2.73,1.44.54,2.89,1.07,4.33,1.62,2.23.85,3.17,2.68,2.57,5-.6,2.31-1.21,4.62-1.84,6.92-.54,1.97-1.93,3.06-3.97,3.08-2.45.03-4.9.02-7.35,0-1.69,0-2.95-.75-3.73-2.27-1.68-3.25-3.35-6.5-5.07-9.73-.36-.68-.25-1.08.36-1.51,1.82-1.28,4.15-.93,5.47.87.39.53.74,1.08,1.11,1.62.1-.02.2-.04.3-.07Z"/>
      <path class="cls-1" d="M25.79,3.6c11.42.17,19.14,11.62,14.93,22.18-.1.25-.19.5-.32.73-.45.82-1.33,1.13-2.13.75-.75-.35-1.05-1.15-.8-2.03.37-1.31.87-2.61,1.05-3.95.92-6.88-4.14-13.44-11-14.34-7.24-.96-13.83,4.03-14.74,11.11-.3,2.35.01,4.59.88,6.77.47,1.19.22,2.09-.69,2.47-.95.4-1.79-.07-2.31-1.3C6.2,15.42,14.29,3.44,25.79,3.6Z"/>
      <path class="cls-1" d="M25.76,9.89c6.46.05,11.14,6.14,9.53,12.4-.31,1.22-1.05,1.77-2.05,1.53-.97-.24-1.35-1.07-1.06-2.32.66-2.89-.41-5.68-2.79-7.28-2.25-1.51-5.32-1.49-7.55.05-2.33,1.61-3.37,4.37-2.71,7.22.29,1.25-.09,2.09-1.05,2.32-1.02.25-1.75-.32-2.07-1.62-1.57-6.3,3.23-12.36,9.75-12.3Z"/>
    </svg>);
}

const getPlanSpeedLabel = (product) => {
  switch (product) {
    case "50/20 Mbps":
      return "47.3";
    case "100/20 Mbps":
    case "100/40 Mbps":
      return "96.4";
    case "250/25 Mbps":
    case "250/100 Mbps":
      return "241.0";
    case "1000/50 Mbps":
    case "1000/400 Mbps":
      return "250+";
  }

  return "47.3";
};

export {
  getPlanSpeedLabel,
};
export default Index;
